import { Markdown } from "@/components/markdown/Markdown.tsx";
import { cn } from "@/lib/utils.ts";
import { messageQueries } from "@/shared/messageQueries.ts";
import type { MessageThreadEvent } from "@/types/threadEvents.ts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import { X } from "lucide-react";
import { useState } from "react";

/**
 * Cancel a queued user message — drops it from the queue before the agent
 * picks it up. The server enforces `status === "queued"` and
 * `userMessageTypes` atomically in its DELETE. On success we refetch the
 * message list; for clientOnly optimistic events that don't have a real
 * server id yet, the parent's `onLocalCancel` removes them from session
 * state directly.
 *
 * `inFlightIds` tracks which pill clicks are pending so each pill disables
 * only its own button — a single shared mutation hook would freeze every
 * cancel button while any one is in flight (BIG-4800 / Greptile P2).
 */
const useCancelQueuedMessage = (threadId: number) => {
  const queryClient = useQueryClient();
  const [inFlightIds, setInFlightIds] = useState<Set<number>>(new Set());

  const mutation = useMutation({
    mutationFn: async (messageId: number) => {
      setInFlightIds((prev) => new Set(prev).add(messageId));
      try {
        await api.post(`/api/a/threads/${threadId}/messages/${messageId}/cancel`);
      } finally {
        setInFlightIds((prev) => {
          const next = new Set(prev);
          next.delete(messageId);
          return next;
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageQueries.thread(threadId) });
    },
  });

  return { mutation, inFlightIds };
};

/**
 * Renders user messages with `status: "queued"` as compact dimmed pills above
 * the OmniBox, while the agent is still processing prior turns. Once the queue
 * loop dequeues them (status flips to `completed`, sortDate is bumped), they
 * drop out of this stack and into the main feed at their new sortDate.
 *
 * Each pill carries a hover-visible X to cancel a queued item before the
 * agent picks it up.
 *
 * Ordering: oldest at top, newest closest to the OmniBox — so newly typed
 * messages stack downward toward the input.
 */
export const QueuedMessageStack = ({
  threadId,
  queuedItems,
  onLocalCancel,
}: {
  threadId: number;
  queuedItems: MessageThreadEvent[];
  /**
   * Called when the user cancels a queued pill that exists only as an
   * optimistic session event (no server id yet). The parent removes it from
   * session state so the pill disappears without waiting for a refetch.
   * Server-confirmed messages go through the cancel endpoint and are removed
   * by the subsequent query invalidation.
   */
  onLocalCancel?: (sessionEventId: number) => void;
}) => {
  const { mutation, inFlightIds } = useCancelQueuedMessage(threadId);

  if (queuedItems.length === 0) return null;

  return (
    <div
      className="flex flex-col gap-1.5 items-end mb-2 px-2 pointer-events-auto"
      data-testid="queued-message-stack"
    >
      {queuedItems.map((item) => {
        // Optimistic events use a random client id (see useSessionChat.onMutate)
        // and don't have a server-side messageId yet. For those, the cancel
        // call would 404 — drop them locally instead.
        const isOptimisticOnly = item.clientOnly?.clientChatId === item.message.id;
        const isCancelling = inFlightIds.has(item.message.id);

        return (
          <div
            key={item.id}
            className={cn(
              "group relative max-w-[75%] @md:max-w-[87%] w-fit",
              "px-4 py-1.5 pr-9 text-sm rounded-3xl",
              "bg-accent/60 text-accent-foreground/70",
              "whitespace-pre-wrap break-words",
            )}
            title="Queued — will be sent when the agent finishes the current turn."
          >
            {/* Markdown so embedded link chips (calendar event / range
                selections) render the same here as in the real bubble after
                the queue drains — otherwise the user briefly sees raw
                `[label](url)` syntax. */}
            <Markdown>{item.message.content}</Markdown>
            <button
              type="button"
              disabled={isCancelling}
              onClick={() => {
                if (isOptimisticOnly) {
                  onLocalCancel?.(item.id);
                  return;
                }
                mutation.mutate(item.message.id, {
                  onSuccess: () => onLocalCancel?.(item.id),
                });
              }}
              aria-label="Cancel queued message"
              className={cn(
                "absolute top-1/2 right-2 -translate-y-1/2",
                "flex items-center justify-center size-5 rounded-full",
                "text-accent-foreground/60 hover:text-accent-foreground",
                "hover:bg-accent-foreground/10 transition-colors",
                "opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
                "disabled:opacity-30 disabled:cursor-not-allowed",
              )}
            >
              <X className="size-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
};
